import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Check, Clock, ExternalLink, Info, MessageCircle, Sparkles, Video } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";
import { useSiteData } from "@/hooks/useSite";
import {
  WEEK_ORDER,
  DAY_LABELS,
  formatDateIt,
  dateIsSelectable,
  romeNow,
  romeToday,
  buildInfoMessage,
  buildBookingWhatsApp,
  whatsappUrl,
} from "@/lib/site";

const STEPS = [
  { n: 1, label: "Servizio" },
  { n: 2, label: "Data" },
  { n: 3, label: "Orario" },
  { n: 4, label: "Nome e invio" },
];

const WEEKDAY_HEADERS = ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];

const NOTICE =
  "La data e l'orario selezionati rappresentano una richiesta di appuntamento, che viene bloccata temporaneamente in attesa di conferma. La prenotazione sarà confermata direttamente da K Nutrition tramite WhatsApp.";

export default function BookingWidget() {
  const { settings, services, whatsappConsentEnabled, isLoading } = useSiteData();
  const [searchParams] = useSearchParams();

  const [serviceId, setServiceId] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [whatsappConsent, setWhatsappConsent] = useState(false);
  const [slotNotice, setSlotNotice] = useState(null);
  const [step, setStep] = useState(1);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [monthDate, setMonthDate] = useState(() => {
    const n = romeNow();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const service = services.find((s) => s.id === serviceId) || null;
  const isOnline = service?.mode === "online";
  const onlineFree = isOnline && !settings.onlineConfigured;
  const today = romeToday();

  useEffect(() => {
    const pre = searchParams.get("servizio");
    if (pre && services.some((s) => s.id === pre && s.active !== false)) {
      setServiceId(pre);
      setStep((prev) => Math.max(prev, 2));
    }
  }, [searchParams, services]);

  useEffect(() => {
    if (!service || !date || onlineFree) {
      setSlots([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingSlots(true);
    api
      .get("/availability", { params: { service_id: service.id, date } })
      .then(({ data }) => {
        if (!cancelled) {
          setSlots(data.slots || []);
          setSlotNotice(data.notice || null);
        }
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [service?.id, date, step]);

  const canNext = {
    1: !!service,
    2: !!date,
    3: !!time,
    4: name.trim().length >= 2
      && phone.trim().replace(/[^0-9]/g, "").length >= 8
      && consent
      && (email.trim() === "" || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())),
  };

  const submit = async () => {
    if (!canNext[4]) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data } = await api.post("/bookings", {
        service_id: service.id,
        date,
        start: time,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        whatsapp_consent: whatsappConsent,
      });
      setBooking(data.booking);
    } catch (e) {
      setSubmitError(apiErrorDetail(e));
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setServiceId(null);
    setDate(null);
    setTime("");
    setName("");
    setPhone("");
    setEmail("");
    setConsent(false);
    setWhatsappConsent(false);
    setBooking(null);
    setSubmitError(null);
    setStep(1);
  };

  /* ---------- calendar ---------- */
  const cal = (() => {
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const firstOffset = (new Date(y, m, 1).getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < firstOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return { y, m, cells };
  })();

  const monthLabel = new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" }).format(monthDate);
  const currentMonthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}`;
  const atCurrentMonth = currentMonthKey === today.slice(0, 7);

  const dayCellState = (d) => {
    const dateStr = `${cal.y}-${String(cal.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const selectable = dateIsSelectable(dateStr, settings, onlineFree ? "online-free" : service?.mode);
    return { dateStr, selectable };
  };

  if (isLoading) {
    return (
      <div className="bg-peach/40 border border-taupe/25 rounded-3xl p-10 animate-pulse" data-testid="booking-loading">
        <p className="font-display text-2xl text-deeptaupe">Preparazione della richiesta…</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="booking-widget">
      {/* steps rail */}
      <aside className="lg:col-span-3">
        <ol className="flex lg:flex-col gap-2 lg:gap-0 overflow-x-auto lg:overflow-visible">
          {STEPS.map((s) => {
            const active = step === s.n;
            const done = step > s.n;
            return (
              <li key={s.n} className="shrink-0 lg:shrink">
                <button
                  onClick={() => {
                    if (done) setStep(s.n);
                  }}
                  disabled={!done && !active}
                  className={`flex items-center gap-3 w-full lg:py-4 lg:px-5 lg:border-l transition-colors duration-300 ${
                    active
                      ? "lg:border-deeptaupe text-charcoal"
                      : done
                        ? "lg:border-taupe text-charcoal/60 hover:text-deeptaupe"
                        : "lg:border-taupe/25 text-charcoal/35"
                  }`}
                  data-testid={`booking-step-indicator-${s.n}`}
                >
                  <span
                    className={`w-7 h-7 rounded-full border text-[12px] inline-flex items-center justify-center font-medium ${
                      active
                        ? "bg-deeptaupe border-deeptaupe text-ivory"
                        : done
                          ? "border-deeptaupe text-deeptaupe"
                          : "border-taupe/40 text-charcoal/40"
                    }`}
                  >
                    {done ? <Check size={14} /> : s.n}
                  </span>
                  <span className="text-[12px] uppercase tracking-widest2 font-medium whitespace-nowrap">{s.label}</span>
                </button>
              </li>
            );
          })}
        </ol>

        {/* live summary */}
        <div className="hidden lg:block mt-8 bg-peach/60 border border-taupe/25 rounded-2xl p-6" data-testid="booking-summary">
          <p className="text-[11px] uppercase tracking-widest2 text-deeptaupe font-medium mb-4">La tua richiesta</p>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-charcoal/50">Servizio</dt>
              <dd className="font-medium">{service ? service.name : "—"}</dd>
            </div>
            <div>
              <dt className="text-charcoal/50">Data</dt>
              <dd className="font-medium">{date ? formatDateIt(date) : "—"}</dd>
            </div>
            <div>
              <dt className="text-charcoal/50">Orario</dt>
              <dd className="font-medium">{time || "—"}</dd>
            </div>
          </dl>
        </div>
      </aside>

      {/* step content */}
      <div className="lg:col-span-9 bg-white rounded-[32px] shadow-soft p-6 lg:p-10 min-h-[480px] flex flex-col">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="flex-1 flex flex-col"
          >
            {step === 1 && (
              <div data-testid="booking-step-service">
                <StepTitle kicker="Passo 1" title="Scegli il servizio" />
                <a
                  href={whatsappUrl(settings.contact.whatsapp, buildInfoMessage(null))}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-info-whatsapp"
                  className="link-underline inline-flex items-center gap-2 mb-6 text-sm text-deeptaupe font-medium"
                >
                  <MessageCircle size={15} /> Non sai quale scegliere? Richiedi informazioni su WhatsApp
                </a>
                <ServiceGroup
                  label="In studio — Salerno"
                  icon={<Sparkles size={16} />}
                  services={services.filter((s) => s.mode === "studio" && s.active !== false)}
                  serviceId={serviceId}
                  onSelect={(id) => {
                    setServiceId(id);
                    setDate(null);
                    setTime("");
                  }}
                />
                <ServiceGroup
                  label="Online"
                  icon={<Video size={16} />}
                  services={services.filter((s) => s.mode === "online" && s.active !== false)}
                  serviceId={serviceId}
                  onSelect={(id) => {
                    setServiceId(id);
                    setDate(null);
                    setTime("");
                  }}
                />
              </div>
            )}

            {step === 2 && (
              <div data-testid="booking-step-date">
                <StepTitle kicker="Passo 2" title="Scegli la data preferita" />
                {onlineFree && (
                  <div className="mb-6 flex gap-3 bg-peach/70 border border-taupe/25 rounded-2xl p-4 text-sm leading-relaxed text-deeptaupe" data-testid="booking-online-notice">
                    <Info size={16} className="shrink-0 mt-0.5" />
                    <p>{settings.onlineNotice}</p>
                  </div>
                )}
                <div className="max-w-md">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => setMonthDate(new Date(cal.y, cal.m - 1, 1))}
                      disabled={atCurrentMonth}
                      aria-label="Mese precedente"
                      data-testid="booking-calendar-prev"
                      className="p-2 rounded-full border border-taupe/30 disabled:opacity-25 hover:bg-peach transition-colors"
                    >
                      <ArrowLeft size={16} />
                    </button>
                    <p className="font-display text-2xl capitalize" data-testid="booking-calendar-month">{monthLabel}</p>
                    <button
                      onClick={() => setMonthDate(new Date(cal.y, cal.m + 1, 1))}
                      aria-label="Mese successivo"
                      data-testid="booking-calendar-next"
                      className="p-2 rounded-full border border-taupe/30 hover:bg-peach transition-colors"
                    >
                      <ArrowRight size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-widest2 text-charcoal/50 mb-2">
                    {WEEKDAY_HEADERS.map((d) => (
                      <span key={d}>{d}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1" data-testid="booking-calendar">
                    {cal.cells.map((d, i) => {
                      if (d === null) return <span key={`e${i}`} />;
                      const { dateStr, selectable } = dayCellState(d);
                      const selected = date === dateStr;
                      return (
                        <button
                          key={dateStr}
                          disabled={!selectable}
                          onClick={() => {
                            setDate(dateStr);
                            setTime("");
                          }}
                          data-testid={`booking-date-${dateStr}`}
                          aria-label={formatDateIt(dateStr)}
                          className={`aspect-square rounded-full text-sm transition-all duration-200 ${
                            selected
                              ? "bg-deeptaupe text-ivory font-medium"
                              : selectable
                                ? "text-charcoal hover:bg-peach hover:scale-105"
                                : "text-charcoal/25 line-through decoration-taupe/40"
                          }`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-4 text-[13px] text-charcoal/70 flex items-center gap-2">
                    <CalendarDays size={14} className="text-taupe" />
                    I giorni non disponibili per lo studio appaiono barrati.
                  </p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="booking-step-time">
                <StepTitle kicker="Passo 3" title={onlineFree ? "Indica l'orario preferito" : "Scegli l'orario di inizio"} />
                {onlineFree ? (
                  <div className="max-w-sm">
                    <label htmlFor="booking-free-time" className="block text-sm text-charcoal/70 mb-2">
                      Orario desiderato
                    </label>
                    <input
                      id="booking-free-time"
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      data-testid="booking-free-time-input"
                      className="w-full rounded-full border border-taupe/40 bg-transparent px-6 py-3.5 text-base focus:border-deeptaupe focus:outline-none"
                    />
                    <p className="mt-4 text-sm text-charcoal/70 leading-relaxed">
                      Le consulenze online hanno orari in definizione: K Nutrition ti proporrà le opzioni effettive
                      rispondendo su WhatsApp.
                    </p>
                  </div>
                ) : loadingSlots ? (
                  <div className="flex items-center gap-3 text-charcoal/70 text-sm py-10" data-testid="booking-slots-loading">
                    <span aria-hidden="true" className="w-4 h-4 rounded-full border-2 border-taupe border-t-transparent animate-spin" />
                    Verifica della disponibilità in tempo reale…
                  </div>
                ) : slots.length === 0 ? (
                  <div className="bg-peach/60 border border-taupe/25 rounded-2xl p-6 text-sm text-deeptaupe" data-testid="booking-no-slots">
                    <p className="font-medium mb-1">Nessun orario disponibile per questa data.</p>
                    <p>
                      Gli orari potrebbero essere già occupati o bloccati: torna indietro e scegli un altro
                      giorno.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-charcoal/60 mb-3 capitalize" data-testid="booking-date-recap">
                      {formatDateIt(date)}
                    </p>
                    {slotNotice && (
                      <p className="mb-4 rounded-2xl bg-peach/70 border border-taupe/30 text-charcoal/80 px-5 py-3 text-[13px]" data-testid="booking-slot-notice">
                        {slotNotice}
                      </p>
                    )}
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-w-xl">
                      {slots.map((hm) => (
                        <button
                          key={hm}
                          onClick={() => setTime(hm)}
                          data-testid={`booking-time-${hm}`}
                          className={`rounded-full border px-3 py-3 text-sm font-medium transition-all duration-200 ${
                            time === hm
                              ? "bg-deeptaupe border-deeptaupe text-ivory"
                              : "border-taupe/40 text-charcoal hover:bg-peach hover:border-taupe"
                          }`}
                        >
                          {hm}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 4 && (
              <div data-testid="booking-step-name">
                {booking ? (
                  <div data-testid="booking-confirmation">
                    <div className="flex items-center gap-4 mb-8">
                      <span className="w-12 h-12 rounded-full bg-peach flex items-center justify-center shrink-0">
                        <Check size={22} className="text-deeptaupe" />
                      </span>
                      <div>
                        <h3 className="font-display text-3xl lg:text-4xl leading-none">Richiesta inviata.</h3>
                        <p className="text-[13px] text-charcoal/60 mt-1">
                          Codice richiesta: <strong data-testid="booking-reference">{booking.id}</strong>
                        </p>
                      </div>
                      <p className="mt-5 text-sm leading-relaxed text-charcoal/85" data-testid="booking-pending-message">
                        Il tuo appuntamento non è ancora confermato. Riceverai conferma da K Nutrition.
                      </p>
                    </div>

                    <div className="bg-peach/60 border border-taupe/25 rounded-2xl p-6 text-sm space-y-1.5" data-testid="booking-confirmation-recap">
                      <p><span className="text-charcoal/55">Servizio:</span> <strong>{booking.service_name}</strong></p>
                      <p><span className="text-charcoal/55">Data:</span> {formatDateIt(booking.date)}</p>
                      <p><span className="text-charcoal/55">Orario:</span> {booking.start}–{booking.end}</p>
                      <p><span className="text-charcoal/55">Stato:</span> <strong>in attesa di conferma</strong></p>
                    </div>

                    <p className="mt-5 flex gap-2.5 text-[13px] leading-relaxed text-charcoal/75" data-testid="booking-pending-note">
                      <Info size={16} className="shrink-0 mt-0.5 text-taupe" />
                      L'orario selezionato è bloccato temporaneamente per te: la richiesta scade automaticamente se
                      non viene confermata. K Nutrition ti risponderà su WhatsApp per confermare l'appuntamento.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => {
                          const message = buildBookingWhatsApp({
                            name: booking.name, service, date: booking.date, time: booking.start, ref: booking.id,
                          });
                          window.open(whatsappUrl(settings.contact.whatsapp, message), "_blank", "noopener,noreferrer");
                        }}
                        data-testid="booking-whatsapp-button"
                        className="inline-flex items-center justify-center gap-3 rounded-full bg-deeptaupe text-ivory text-sm uppercase tracking-widest2 font-medium px-10 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:bg-charcoal"
                      >
                        <MessageCircle size={18} /> Avvisa su WhatsApp
                      </button>
                      <button
                        onClick={resetAll}
                        data-testid="booking-reset"
                        className="inline-flex items-center justify-center rounded-full border border-taupe/40 text-charcoal/70 text-sm uppercase tracking-widest2 font-medium px-8 py-4 transition-colors duration-300 hover:bg-peach"
                      >
                        Nuova richiesta
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <StepTitle kicker="Passo 4" title="Come ti chiami?" />
                    <div className="max-w-sm space-y-5">
                      <div>
                        <label htmlFor="booking-name" className="block text-sm text-charcoal/70 mb-2">
                          Il tuo nome
                        </label>
                        <input
                          id="booking-name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Nome e cognome"
                          autoComplete="name"
                          data-testid="booking-name-input"
                          className="w-full rounded-full border border-taupe/40 bg-transparent px-6 py-3.5 text-base placeholder:text-charcoal/35 focus:border-deeptaupe focus:outline-none"
                        />
                      </div>
                      <div>
                        <label htmlFor="booking-phone" className="block text-sm text-charcoal/70 mb-2">
                          Il tuo telefono (per la conferma)
                        </label>
                        <input
                          id="booking-phone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+39 ..."
                          autoComplete="tel"
                          data-testid="booking-phone-input"
                          className="w-full rounded-full border border-taupe/40 bg-transparent px-6 py-3.5 text-base placeholder:text-charcoal/35 focus:border-deeptaupe focus:outline-none"
                        />
                      </div>
                      <div>
                        <label htmlFor="booking-email" className="block text-sm text-charcoal/70 mb-2">
                          Email (facoltativa)
                        </label>
                        <input
                          id="booking-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="nome@email.it"
                          autoComplete="email"
                          data-testid="booking-email-input"
                          className="w-full rounded-full border border-taupe/40 bg-transparent px-6 py-3.5 text-base placeholder:text-charcoal/35 focus:border-deeptaupe focus:outline-none"
                        />
                      </div>
                      <label htmlFor="booking-privacy" className="flex items-start gap-3 text-[13px] leading-relaxed text-charcoal/75 cursor-pointer">
                        <input
                          id="booking-privacy"
                          type="checkbox"
                          checked={consent}
                          onChange={(e) => setConsent(e.target.checked)}
                          data-testid="booking-privacy-checkbox"
                          className="mt-0.5 w-4 h-4 shrink-0 accent-[#6B5C50]"
                        />
                        <span>
                          Ho letto e accetto l&apos;
                          <Link to="/privacy" className="underline underline-offset-2 text-deeptaupe font-medium" data-testid="booking-privacy-link">
                            informativa sulla privacy
                          </Link>
                          . Non condividere dati sulla tua salute in questo modulo.
                        </span>
                      </label>
                      {whatsappConsentEnabled && (
                        <label htmlFor="booking-wa-consent" className="flex items-start gap-3 text-[13px] leading-relaxed text-charcoal/75 cursor-pointer" data-testid="booking-wa-consent-label">
                          <input
                            id="booking-wa-consent"
                            type="checkbox"
                            checked={whatsappConsent}
                            onChange={(e) => setWhatsappConsent(e.target.checked)}
                            data-testid="booking-wa-consent-checkbox"
                            className="mt-0.5 w-4 h-4 shrink-0 accent-[#6B5C50]"
                          />
                          <span>Acconsento a ricevere su WhatsApp la conferma e il promemoria del mio appuntamento.</span>
                        </label>
                      )}
                    </div>

                    <div className="mt-8 bg-peach/60 border border-taupe/25 rounded-2xl p-6" data-testid="booking-recap">
                      <p className="text-[11px] uppercase tracking-widest2 text-deeptaupe font-medium mb-3">Riepilogo</p>
                      <ul className="space-y-1.5 text-sm">
                        <li><span className="text-charcoal/55">Servizio:</span> <strong className="font-medium">{service.name}</strong></li>
                        <li><span className="text-charcoal/55">Data desiderata:</span> {formatDateIt(date)}</li>
                        <li><span className="text-charcoal/55">Orario desiderato:</span> {time}</li>
                      </ul>
                    </div>

                    {submitError && (
                      <div className="mt-6 border border-red-200 bg-red-50 text-red-700 rounded-2xl p-5 text-sm leading-relaxed" data-testid="booking-error">
                        {submitError}
                        <button onClick={() => setStep(3)} data-testid="booking-error-back-to-times" className="ml-2 underline underline-offset-2 font-medium">
                          Scegli un altro orario
                        </button>
                      </div>
                    )}

                    <button
                      onClick={submit}
                      disabled={!canNext[4] || submitting}
                      data-testid="booking-submit-button"
                      className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-deeptaupe text-ivory text-sm uppercase tracking-widest2 font-medium px-10 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:bg-charcoal disabled:opacity-30 disabled:hover:translate-y-0"
                    >
                      {submitting ? (
                        <>
                          <span aria-hidden="true" className="w-4 h-4 rounded-full border-2 border-ivory/40 border-t-ivory animate-spin" />
                          Registrazione…
                        </>
                      ) : (
                        <>
                          <Check size={18} /> Invia richiesta
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* nav buttons */}
        <div className="flex items-center justify-between pt-8 mt-8 border-t border-taupe/20">
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              data-testid={`booking-back-${step}`}
              className="inline-flex items-center gap-2 text-sm uppercase tracking-widest2 font-medium text-charcoal/60 hover:text-deeptaupe transition-colors"
            >
              <ArrowLeft size={15} /> Indietro
            </button>
          ) : (
            <span />
          )}
          {step < 4 && (
            <button
              onClick={() => canNext[step] && setStep(step + 1)}
              disabled={!canNext[step]}
              data-testid={`booking-next-${step}`}
              className="inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-sm uppercase tracking-widest2 font-medium px-8 py-3.5 transition-all duration-300 hover:bg-deeptaupe hover:text-ivory disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-deeptaupe"
            >
              Continua <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>

      {/* notice */}
      <div className="lg:col-span-12 bg-peach/50 border border-taupe/25 rounded-2xl p-5 flex gap-3 text-sm leading-relaxed text-charcoal/75" data-testid="booking-notice">
        <Clock size={16} className="shrink-0 mt-0.5 text-taupe" />
        <p>
          {NOTICE}{" "}
          <span className="text-charcoal/60">
            Nessun pagamento avviene sul sito.
          </span>
        </p>
      </div>
    </div>
  );
}

function StepTitle({ kicker, title }) {
  return (
    <div className="mb-8">
      <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium mb-2">{kicker}</p>
      <h3 className="font-display text-3xl lg:text-4xl">{title}</h3>
    </div>
  );
}

function ServiceGroup({ label, icon, services, serviceId, onSelect }) {
  if (!services.length) return null;
  return (
    <div className="mb-8" data-testid={`booking-group-${label.toLowerCase().split(" ")[0]}`}>
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest2 text-charcoal/55 font-medium mb-3">
        {icon} {label}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {services.map((s) => {
          const selected = serviceId === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onSelect(s.id)}
              data-testid={`booking-service-${s.id}`}
              aria-pressed={selected}
              className={`text-left rounded-2xl border p-5 transition-all duration-300 ${
                selected
                  ? "border-deeptaupe bg-peach shadow-[0_2px_16px_rgba(107,92,80,0.12)]"
                  : "border-taupe/35 hover:border-taupe hover:bg-peach/40"
              }`}
            >
              <span className="block font-display text-xl leading-snug">{s.name}</span>
              <span className="mt-2 flex items-center gap-2 text-[13px] text-charcoal/70">
                {s.mode === "online" ? "Online" : "In studio"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
